import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Sparkles, Layers } from "lucide-react";
import { formatPhoneNumber } from "@/lib/phoneUtils";

// US States for dropdown
const US_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
];

interface EditSubcontractorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (subcontractor: any) => void;
  subcontractor?: {
    id: string;
    name: string;
    contactName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    website: string;
    services: string[];
    notes: string;
    status: string;
  };
}

export function EditSubcontractorSheet({
  isOpen,
  onClose,
  onSave = () => {},
  subcontractor,
}: EditSubcontractorSheetProps) {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    contactName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    website: "",
    services: [] as string[],
    cleaningServices: [] as string[],
    floorcareServices: [] as string[],
    notes: "",
    status: "pending",
  });

  const [newService, setNewService] = useState("");
  const [customService, setCustomService] = useState("");
  const [newCleaningService, setNewCleaningService] = useState("");
  const [customCleaningService, setCustomCleaningService] = useState("");
  const [newFloorcareService, setNewFloorcareService] = useState("");
  const [customFloorcareService, setCustomFloorcareService] = useState("");

  useEffect(() => {
    if (subcontractor) {
      setFormData({
        id: subcontractor.id || "",
        name: subcontractor.name || "",
        contactName: subcontractor.contactName || "",
        email: subcontractor.email || "",
        phone: subcontractor.phone || "",
        address: subcontractor.address || "",
        city: subcontractor.city || "",
        state: subcontractor.state || "",
        zipCode: subcontractor.zipCode || "",
        website: subcontractor.website || "",
        services: subcontractor.services || [],
        cleaningServices: [], // Initialize empty, will be populated from services if needed
        floorcareServices: [], // Initialize empty, will be populated from services if needed
        notes: subcontractor.notes || "",
        status: subcontractor.status || "pending",
      });
    }
  }, [subcontractor, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (id: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({
      ...prev,
      phone: formattedPhone,
    }));
  };

  const handleAddService = () => {
    if (newService === "custom") {
      if (customService.trim() !== "") {
        setFormData((prev) => ({
          ...prev,
          services: [...prev.services, customService],
        }));
        setCustomService("");
      }
    } else if (newService) {
      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, newService],
      }));
    }
    setNewService("");
  };

  const handleRemoveService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s !== service),
    }));
  };

  const handleAddCleaningService = () => {
    if (newCleaningService === "custom") {
      if (customCleaningService.trim() !== "") {
        setFormData((prev) => ({
          ...prev,
          cleaningServices: [...prev.cleaningServices, customCleaningService],
        }));
        setCustomCleaningService("");
      }
    } else if (newCleaningService) {
      setFormData((prev) => ({
        ...prev,
        cleaningServices: [...prev.cleaningServices, newCleaningService],
      }));
    }
    setNewCleaningService("");
  };

  const handleRemoveCleaningService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      cleaningServices: prev.cleaningServices.filter((s) => s !== service),
    }));
  };

  const handleAddFloorcareService = () => {
    if (newFloorcareService === "custom") {
      if (customFloorcareService.trim() !== "") {
        setFormData((prev) => ({
          ...prev,
          floorcareServices: [...prev.floorcareServices, customFloorcareService],
        }));
        setCustomFloorcareService("");
      }
    } else if (newFloorcareService) {
      setFormData((prev) => ({
        ...prev,
        floorcareServices: [...prev.floorcareServices, newFloorcareService],
      }));
    }
    setNewFloorcareService("");
  };

  const handleRemoveFloorcareService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      floorcareServices: prev.floorcareServices.filter((s) => s !== service),
    }));
  };

  const handleSubmit = () => {
    // Combine all services into the main services array for backward compatibility
    const allServices = [
      ...formData.services,
      ...formData.cleaningServices,
      ...formData.floorcareServices,
    ];

    const submissionData = {
      ...formData,
      services: allServices,
    };

    onSave(submissionData);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Edit Subcontractor</SheetTitle>
          <SheetDescription>Update details for this subcontractor.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  placeholder="Enter contact name"
                  value={formData.contactName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="(555) 555-5555"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website (Optional)</Label>
                <Input
                  id="website"
                  placeholder="Enter website URL"
                  value={formData.website}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Enter street address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select
                  onValueChange={(value) => handleSelectChange("state", value)}
                  value={formData.state}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name} - {state.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipCode">ZIP Code</Label>
                <Input
                  id="zipCode"
                  placeholder="Enter ZIP code"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Services Offered</h3>

              {/* Cleaning Services */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                    <Sparkles className="h-4 w-4 text-blue-500" /> Cleaning Services
                  </h3>

                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.cleaningServices.map((service, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1 text-sm"
                      >
                        {service}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1 h-4 w-4 p-0"
                          onClick={() => handleRemoveCleaningService(service)}
                        >
                          <X className="h-3 w-3 text-gray-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Select value={newCleaningService} onValueChange={setNewCleaningService}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a cleaning service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Office Cleaning">Office Cleaning</SelectItem>
                        <SelectItem value="Restroom Cleaning">Restroom Cleaning</SelectItem>
                        <SelectItem value="Kitchen & Break Room Cleaning">
                          Kitchen & Break Room Cleaning
                        </SelectItem>
                        <SelectItem value="Window Cleaning">Window Cleaning</SelectItem>
                        <SelectItem value="Pressure Washing">Pressure Washing</SelectItem>
                        <SelectItem value="Post-Construction Cleaning">
                          Post-Construction Cleaning
                        </SelectItem>
                        <SelectItem value="Deep Cleaning">Deep Cleaning</SelectItem>
                        <SelectItem value="Disinfection Services">Disinfection Services</SelectItem>
                        <SelectItem value="Green Cleaning">Green Cleaning</SelectItem>
                        <SelectItem value="High-Touch Surface Sanitization">
                          High-Touch Surface Sanitization
                        </SelectItem>
                        <SelectItem value="Equipment & Machinery Cleaning">
                          Equipment & Machinery Cleaning
                        </SelectItem>
                        <SelectItem value="Specialty Cleaning">Specialty Cleaning</SelectItem>
                        <SelectItem value="Move-In/Move-Out Cleaning">
                          Move-In/Move-Out Cleaning
                        </SelectItem>
                        <SelectItem value="custom">Add Custom Service</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddCleaningService}
                      disabled={!newCleaningService}
                      size="default"
                      className="px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {newCleaningService === "custom" && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom cleaning service"
                        value={customCleaningService}
                        onChange={(e) => setCustomCleaningService(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                {/* Floorcare Services */}
                <div>
                  <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                    <Layers className="h-4 w-4 text-blue-500" /> Floorcare Services
                  </h3>

                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.floorcareServices.map((service, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1 text-sm"
                      >
                        {service}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1 h-4 w-4 p-0"
                          onClick={() => handleRemoveFloorcareService(service)}
                        >
                          <X className="h-3 w-3 text-gray-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Select value={newFloorcareService} onValueChange={setNewFloorcareService}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a floorcare service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Floor Stripping">Floor Stripping</SelectItem>
                        <SelectItem value="Floor Waxing">Floor Waxing</SelectItem>
                        <SelectItem value="Floor Buffing">Floor Buffing</SelectItem>
                        <SelectItem value="Floor Refinishing">Floor Refinishing</SelectItem>
                        <SelectItem value="Tile & Grout Cleaning">Tile & Grout Cleaning</SelectItem>
                        <SelectItem value="Hardwood Floor Care">Hardwood Floor Care</SelectItem>
                        <SelectItem value="Concrete Floor Polishing">
                          Concrete Floor Polishing
                        </SelectItem>
                        <SelectItem value="VCT Floor Maintenance">VCT Floor Maintenance</SelectItem>
                        <SelectItem value="Epoxy Floor Coating">Epoxy Floor Coating</SelectItem>
                        <SelectItem value="Floor Sealing">Floor Sealing</SelectItem>
                        <SelectItem value="custom">Add Custom Service</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddFloorcareService}
                      disabled={!newFloorcareService}
                      size="default"
                      className="px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {newFloorcareService === "custom" && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom floorcare service"
                        value={customFloorcareService}
                        onChange={(e) => setCustomFloorcareService(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleSelectChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Enter any additional notes about this subcontractor"
                rows={4}
                value={formData.notes}
                onChange={handleInputChange}
              />
            </div>

            <Button className="mt-6 w-full" onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
